import { Component, Input, OnInit, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    AbstractControl,
    ControlValueAccessor,
    FormBuilder,
    FormGroup,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    ValidationErrors,
    Validator,
    Validators,
} from '@angular/forms';
import { PasswordModule } from 'primeng/password';

export function strongPasswordValidator(): ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;

        if (!value) {
            return null;
        }
        const passwordRegex = /^(?=.*[A-Z].*[A-Z])(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])(?=.*[0-9])(?=.{12,}).*$/;
        const hasMinLength = value.length >= 12;
        const hasUppercaseLetters = (value.match(/[A-Z]/g) || []).length >= 2;
        const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value);
        const hasDigit = /[0-9]/.test(value);

        if (!passwordRegex.test(value)) {
            return {
                strongPassword: true,
                hasMinLength,
                hasUppercaseLetters,
                hasSpecialChar,
                hasDigit,
            };
        }

        return null;
    };
}

@Component({
    selector: 'qc-password-form',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, PasswordModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PasswordFormComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => PasswordFormComponent),
            multi: true,
        },
    ],
    styles: `
        .password-requirements {
            font-size: 0.875rem;
            margin-top: 10px;
            ul {
                padding: 0 0 0 5px;
                margin-top: 5px;
            }
            li {
                margin-bottom: 1px;
                position: relative;
                padding-left: 20px;
            }
            li::before {
                font-family: system-ui;
                content: '•';
                position: absolute;
                left: 0;
                top: -2px;
                font-size: 1.2em;
            }
            .text-lime-500::before {
                content: '✓';
            }

            .text-rose-500::before {
                content: '✗';
            }
        }
    `,
    template: `
        <div [formGroup]="passwordForm">
            <p-password
                [id]="id"
                formControlName="password"
                [toggleMask]="true"
                styleClass="w-full"
                inputStyleClass="w-full"
                [placeholder]="placeholder"
                [size]="size"
                [ngClass]="{ 'ng-invalid ng-dirty': submitted && passwordForm.controls.password.errors }"
                [feedback]="false"
            />
            <div class="password-requirements mt-2">
                <ul class="list-unstyled ps-3">
                    <li [ngClass]="getRequirementClass('hasMinLength')">At least 12 characters</li>
                    <li [ngClass]="getRequirementClass('hasUppercaseLetters')">At least 2 uppercase letters</li>
                    <li [ngClass]="getRequirementClass('hasSpecialChar')">
                        At least 1 special character (!&#64;#$%^&*...)
                    </li>
                    <li [ngClass]="getRequirementClass('hasDigit')">At least 1 number</li>
                </ul>
            </div>
            <small
                *ngIf="submitted && passwordForm.controls.password.errors?.['required']"
                class="text-rose-500 text-xs mt-1 block"
            >
                Password is required
            </small>
        </div>
    `,
})
export class PasswordFormComponent implements ControlValueAccessor, Validator, OnInit {
    @Input() id = 'password';
    @Input() placeholder = 'Create a strong password';
    @Input() size: 'large' | 'small' = 'large';
    @Input() submitted = false;

    passwordForm: FormGroup;

    private onChange: any = () => {};
    private onTouched: any = () => {};

    constructor(private fb: FormBuilder) {
        this.passwordForm = this.fb.group({
            password: ['', [Validators.required, strongPasswordValidator()]],
        });
    }

    ngOnInit(): void {
        this.passwordForm.controls.password.valueChanges.subscribe((value) => {
            this.onChange(value);
        });
    }

    writeValue(value: any): void {
        if (value !== undefined) {
            this.passwordForm.controls.password.setValue(value, { emitEvent: false });
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        if (this.passwordForm.controls.password.invalid) {
            return { invalidPassword: true };
        }
        return null;
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.passwordForm.disable();
        } else {
            this.passwordForm.enable();
        }
    }

    getRequirementClass(requirement: string): string {
        const passwordControl = this.passwordForm.controls.password;
        if (!passwordControl.value) {
            return 'opacity-50';
        }
        const value = String(passwordControl.value);
        switch (requirement) {
            case 'hasMinLength':
                return value.length >= 12 ? 'text-lime-500' : 'text-rose-500';
            case 'hasUppercaseLetters':
                return (value.match(/[A-Z]/g) || []).length >= 2 ? 'text-lime-500' : 'text-rose-500';
            case 'hasSpecialChar':
                return /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value) ? 'text-lime-500' : 'text-rose-500';
            case 'hasDigit':
                return /[0-9]/.test(value) ? 'text-lime-500' : 'text-rose-500';
            default:
                return 'text-muted';
        }
    }
}
